import React, { useEffect, useMemo, useRef, useState } from "react";
import './styles.css';
import Card from "../Card";
import DateTimeInput from "../DateTimeInput";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFloppyDisk, faXmark } from "@fortawesome/free-solid-svg-icons";
import FloatingActions, { FloatingAction } from "../FloatingActions";
import useRouter from "@/store/useRouter";
import { useShallow } from "zustand/shallow";
import { RouterStoreItemKey } from "@/store/useRouter/types";
import useAppData from "@/store/useAppData";
import axios from "axios";
import useDeviceType from "@/Utils/useDeviceType";
import FlightListComp from "../FlightList";
import DialogWindow from "../DialogWindow";
import { DeicingItem } from "../DeicingList/types";
import { StockItem } from "../StockingDeicingCard";
import { Stock } from "../UserSettings/types";
import { StockMovement } from "@/Utils/constants";
import { toLocalDateTimeOffset } from "../DeicingForm";

export interface DeicingFormTechData {
    type1FluidConsumption: number | null;
    type2FluidConsumption: number | null;
    waterConsumption: number | null;
    note?: string | null;
    date?: string | null;
}

export interface DeicingFormItem {
    type1FluidConsumption: number | null;
    fluidRatio: string | null;
    waterConsumption: number | null;
    note?: string | null;
}

const initialFormData = (
): DeicingFormTechData => ({
    type1FluidConsumption: null,
    type2FluidConsumption: null,
    waterConsumption: null,
    note: ""
});

const DeicingFormTech: React.FC = () => {

    const loggedUser = useAppData(state => state.user);
    const [formData, setFormData] = useState<DeicingFormTechData>();
    const [stocksState, setStocksState] = React.useState<Stock[]>([]);
    const selectedModule = useAppData(state => state.selectedModule);
    const [selectedStock, setSelectedStock] = React.useState<string | null>(null);

    const router = useRouter(
        useShallow(state => ({
            go: state.go,
            history: state.history
        }))
    );

    useEffect(() => {

        const init = async () => {
            await loadStocks();
        };

        init();
    }, []);

    const handleSubmit = async () => {

        if (selectedStock && formData?.date) {
            await updateMainStockStockItems();

            router.go({ key: RouterStoreItemKey.LISTITEMS });
        }
    };

    const actions: FloatingAction[] = [
        {
            key: "cancel",
            label: "Zrušit",
            // icon: <FontAwesomeIcon icon={faXmark} />,
            onClick: () => {
                router.go({ key: RouterStoreItemKey.LISTITEMS });
            },
        },
        {
            key: "save",
            label: "Uložit",
            // icon: <FontAwesomeIcon icon={faFloppyDisk} />,
            onClick: () => handleSubmit(),
        }
    ];

    const updateField = <K extends keyof DeicingFormTechData>(
        field: K,
        value: DeicingFormTechData[K]
    ) => {
        let updatedFormData;
        setFormData(previous => {
            updatedFormData = {
                ...previous,
                [field]: value,
                lastModified: toLocalDateTimeOffset(new Date()),
                modifiedBy: Number(loggedUser?.id!),
            }
            return updatedFormData;
        });
    };

    const updateMainStockStockItems = async () => {
        const mainStock: Stock = await getMainStock();
        const stockItemsMainStock = await getStockItems(mainStock.id);

        //const stocksItemsWithoutIBC = stockItemsMainStock.filter(stockItem => stockItem.name != "IBC kontejner");

        // for (const stockItem of stocksItemsWithoutIBC) {
        //     const fluidConsumption = stockItem.name === "Typ 1" ? formData?.type1FluidConsumption || 0 : stockItem.name === "Typ 2" ? formData?.type2FluidConsumption || 0 : formData?.waterConsumption || 0;

        //     if (fluidConsumption > 0) {

        //         if (stockItem.autofill) {
        //             await stockMovement(mainStock.id, stockItem.id, String(StockMovement.Issue), fluidConsumption, loggedUser!.id, formData?.note ?? undefined, formData?.date!);
        //             await stockMovement(selectedStock!, stockItem.id, String(StockMovement.StockIn), fluidConsumption, loggedUser!.id, formData?.note ?? undefined, formData?.date!);
        //             await stockMovement(selectedStock!, stockItem.id, String(StockMovement.Issue), fluidConsumption, loggedUser!.id, formData?.note ?? undefined, formData?.date!);
        //             await stockMovement(mainStock.id, stockItem.id, String(StockMovement.StockIn), fluidConsumption, loggedUser!.id, formData?.note ?? undefined, formData?.date!);
        //         } else {
        //             await stockMovement(mainStock.id, stockItem.id, String(StockMovement.Issue), fluidConsumption, loggedUser!.id, formData?.note ?? undefined, formData?.date!);
        //             await stockMovement(selectedStock!, stockItem.id, String(StockMovement.StockIn), fluidConsumption, loggedUser!.id, formData?.note ?? undefined, formData?.date!);
        //             await stockMovement(selectedStock!, stockItem.id, String(StockMovement.Issue), fluidConsumption, loggedUser!.id, formData?.note ?? undefined, formData?.date!);
        //         }
        //     }
        // }

        await Promise.all(
            stockItemsMainStock
                .filter(stockItem => stockItem.name != "IBC kontejner")
                .map(async stockItem => {
                    const fluidConsumption = stockItem.name === "Kapalina I" ? formData?.type1FluidConsumption || 0 : stockItem.name === "Kapalina II" ? formData?.type2FluidConsumption || 0 : formData?.waterConsumption || 0;

                    if (fluidConsumption > 0) {

                        if (stockItem.autofill) {
                            await stockMovement(mainStock.id, stockItem.id, String(StockMovement.Issue), fluidConsumption, loggedUser!.id, formData?.note ?? undefined, formData?.date!);
                            await stockMovement(selectedStock!, stockItem.id, String(StockMovement.StockIn), fluidConsumption, loggedUser!.id, formData?.note ?? undefined, formData?.date!);
                            await stockMovement(selectedStock!, stockItem.id, String(StockMovement.Issue), fluidConsumption, loggedUser!.id, formData?.note ?? undefined, formData?.date!);
                            await stockMovement(mainStock.id, stockItem.id, String(StockMovement.StockIn), fluidConsumption, loggedUser!.id, formData?.note ?? undefined, formData?.date!);
                        } else {
                            await updateStockItemDecrement(stockItem.id, fluidConsumption);
                            await stockMovement(mainStock.id, stockItem.id, String(StockMovement.Issue), fluidConsumption, loggedUser!.id, formData?.note ?? undefined, formData?.date!);
                            await stockMovement(selectedStock!, stockItem.id, String(StockMovement.StockIn), fluidConsumption, loggedUser!.id, formData?.note ?? undefined, formData?.date!);
                            await stockMovement(selectedStock!, stockItem.id, String(StockMovement.Issue), fluidConsumption, loggedUser!.id, formData?.note ?? undefined, formData?.date!);
                        }
                    }
                })
        );
    }

    const getMainStock = async () => {

        try {
            const res = await (
                axios({
                    method: "post",
                    url: "api/stock/getAll",
                    data: {
                        mainStock: true
                    }
                })
            );

            if (!res?.data?.stocks?.length || res?.data?.stocks?.length === 0) {
                return null;
            }

            return res?.data?.stocks[0];

        } catch (err) {
            throw err;
        }
    }

    const loadStocks = async () => {

        try {
            const res = await (
                axios({
                    method: "post",
                    url: "api/stock/getAll",
                    data: {
                        module: selectedModule?.id
                    }
                })
            );

            setStocksState(res.data.stocks);

            updateField("date", toLocalDateTimeOffset(new Date()));

        } catch (err) {
            throw err;
        }
    }

    const getStockItems = async (stockId: string) => {
        try {
            const res = await (
                axios({
                    method: "post",
                    url: "api/stockitem/getAll",
                    data: {
                        start: 0,
                        end: 100,
                        stockId: stockId
                    }
                })
            );

            return res.data.stocksItems;
        } catch (err) {
            console.log(err);
            throw err;
        }
    }

    const stockMovement = async (stockId: string, stockItemId: string, stockMovementId: string, quantity: number, userId: string, note?: string, date?: string) => {
        try {
            const res = await axios({
                method: "post",
                url: "/api/log/stockMoveOdmraz",
                data: {
                    stockId: stockId,
                    stockItemId: stockItemId,
                    quantity: quantity,
                    stockMovementId: stockMovementId,
                    userId: userId,
                    note: note,
                    techDeicing: true,
                    date: date
                },
            });

            return res.data.id;
        } catch (err) {
            console.error("Failed to make stock movement item", err);
            throw err;
        }
    };

    const updateStockItemDecrement = async (stockItemId: string, qauntity: number) => {
        try {
            const res = await axios({
                method: "post",
                url: "/api/stockitem/updateDecrement",
                data: {
                    id: stockItemId,
                    quantity: qauntity
                },
            });

            return res.data.id;
        } catch (err) {
            console.error("Failed to update stock item decrement", err);
            throw err;
        }
    };

    return <Card
        title="Technologický odmraz"
        displayLogo={false}
        displaySelectedStock={false}
    >
        <form className="deicing-form">

            <section className="form-section">
                <h3>Kapaliny</h3>

                <div className="form-grid-tech">
                    <div className="form-grid-item deice-item">
                        <SelectField
                            label="Vozidlo"
                            value={selectedStock}
                            options={stocksState.filter(stock => !stock?.main && stock?.id && stock?.name).map(stock => ({ value: stock?.id!, label: stock?.name! }))}
                            onChange={value => setSelectedStock(value)}
                        />

                        <NumberField
                            label="Spotřeba kapaliny I [litr]"
                            value={formData?.type1FluidConsumption ?? null}
                            min={0}
                            onChange={value =>
                                updateField("type1FluidConsumption", value)
                            }
                        />

                        <NumberField
                            label="Spotřeba kapaliny II [litr]"
                            value={formData?.type2FluidConsumption ?? null}
                            min={0}
                            onChange={value =>
                                updateField("type2FluidConsumption", value)
                            }
                        />

                        <NumberField
                            label="Spotřeba vody [litr]"
                            value={formData?.waterConsumption ?? null}
                            min={0}
                            onChange={value =>
                                updateField("waterConsumption", value)
                            }
                        />

                        <DateTimeField
                            label="Datum"
                            value={formData?.date ?? null}
                            onChange={value => updateField("date", value)}
                            onSetCurrentTime={() => toLocalDateTimeOffset(new Date())}
                        />

                        <label className="wheatherConditions">
                            Poznámka
                            <textarea
                                rows={4}
                                value={formData?.note ?? undefined}
                                onChange={event => updateField("note", event.target.value)}
                            />
                        </label>
                    </div>
                </div>
            </section>

            <div className="item-list-actions">
                <FloatingActions actions={actions} />
            </div>

        </form>
    </Card>
};

interface NumberFieldProps {
    label: string;
    value: number | null;
    min?: number;
    max?: number;
    onChange: (value: number | null) => void;
    readOnly?: boolean;
}

const NumberField: React.FC<NumberFieldProps> = ({
    label,
    value,
    min,
    max,
    onChange,
    readOnly = false
}) => (
    <label>
        {label}
        <input
            type="number"
            value={value ?? undefined}
            min={min}
            max={max}
            step={1}
            onChange={event =>
                onChange(
                    event.target.value === ""
                        ? null
                        : Number(event.target.value)
                )
            }
            readOnly={readOnly}
        />
    </label>
);

interface Option {
    value: string;
    label: string;
}

interface SelectFieldProps {
    label: string;
    value: string | number | null | undefined;
    options: Option[];
    onChange: (value: string) => void;
    disabled?: boolean;
}

const SelectField: React.FC<SelectFieldProps> = ({
    label,
    value,
    options,
    onChange,
    disabled = false
}) => (
    <label>
        {label}
        <select
            value={value ?? undefined}
            onChange={event => onChange(event.target.value)}
            disabled={disabled}
        >
            <option value="">Vyberte hodnotu</option>

            {options.map(option => (
                <option key={option.value} value={option.value}>
                    {option.label}
                </option>
            ))}
        </select>
    </label>
);

interface DateTimeFieldProps {
    label: string;
    value: string | null;
    onChange: (value: string | null) => void;
    onSetCurrentTime: () => void;
    disabled?: boolean;
}

const DateTimeField: React.FC<DateTimeFieldProps> = ({
    label,
    value,
    onChange,
    onSetCurrentTime,
    disabled = false,
}) => {
    return (
        <div className="date-time-field">
            <div className="date-time-field__label">
                <span className="date-time-field__label-text">
                    {label}
                </span>

                <DateTimeInput
                    className="date-time-field__input"
                    aria-label={label}
                    value={value ?? ""}
                    disabled={disabled}
                    onChange={value => onChange(value || null)}
                />
            </div>

            {/* <button
                className="date-time-field__button"
                type="button"
                disabled={disabled}
                onClick={onSetCurrentTime}
            >
                Nastavit aktuální čas
            </button> */}
        </div>
    );
};

export default DeicingFormTech;
